import { useCallback, useEffect, useState } from "react";

import classNames from "classnames";

import { Button } from "../../../../../../packages/ui/src/components/Button/Button";
import { ProgressBar } from "../../../../../../packages/ui/src/components/ProgressBar/ProgressBar";
import usePrevious from "../../../../../../packages/ui/src/hooks/usePrevious";
import useTranslation from "../../../hooks/useTranslation";
import { Modal, type ModalName } from "../../../lib/modal";

import style from "./FeatureTour.module.css";

interface FeatureTourSlideProps {
  isOpen: boolean;
  slides: FeatureTourSlideItem[];
  completeLabel: string;
  closeOnFinish?: boolean;
  restartOnClose?: boolean;
  onComplete?: () => void;
  onClose?: () => void;
  telemetryName: ModalName;
}

export interface FeatureTourSlideItem {
  img: string;
  title: string;
  description: string;
  onNext?: () => void;
  onDisplay?: () => void;
}

export const FeatureTour = ({
  isOpen,
  slides,
  completeLabel,
  closeOnFinish,
  restartOnClose,
  onComplete,
  onClose,
  telemetryName,
}: FeatureTourSlideProps) => {
  const { t, m } = useTranslation();

  const [currentSlide, setCurrentSlide] = useState(0);
  const previousCurrent = usePrevious(currentSlide);
  useEffect(() => {
    if (isOpen) {
      slides[currentSlide].onDisplay?.();
    }
  }, [currentSlide, slides, isOpen]);

  const handleFinish = () => {
    onComplete?.();
    if (closeOnFinish) {
      handleClose();
    }
  };

  const handleNext = (slideIndex: number) => {
    slides[slideIndex]?.onNext?.();
  };

  const handleClose = useCallback(() => {
    if (restartOnClose) {
      setCurrentSlide(0);
    }
    onClose?.();
  }, [onClose, restartOnClose]);

  return (
    <Modal
      open={isOpen}
      className={style.tourModal}
      onClose={handleClose}
      telemetryName={telemetryName}
    >
      <div className={style.featureTour}>
        <div className={style.imageWrapper}>
          {slides.map((element, index) => {
            return (
              <div
                key={`${element.title}-${element.description}`}
                className={classNames(
                  style.item,
                  index === currentSlide && [style.active, style.fadeIn],
                  index === previousCurrent && style.fadeOut
                )}
              >
                <img className={style.image} src={element.img} />
              </div>
            );
          })}
        </div>
        <div className={style.content}>
          {slides.map((element, index) => {
            return (
              <div
                key={`${element.title}-${element.description}`}
                className={classNames(
                  style.itemContent,
                  index === currentSlide && [style.active, style.fadeIn],
                  index === previousCurrent && style.fadeOut
                )}
              >
                <div className={style.title}>{element.title}</div>
                <div>{element.description}</div>
              </div>
            );
          })}

          <div className={style.progress}>
            <div style={{ marginRight: "auto" }}>
              <ProgressBar percentage={currentSlide / (slides.length - 1)} />
            </div>
            <Button
              onClick={() => {
                if (currentSlide < slides.length - 1) {
                  handleNext(currentSlide);
                  setCurrentSlide((prev) => {
                    return prev + 1;
                  });
                } else {
                  handleFinish();
                }
              }}
            >
              {currentSlide < slides.length - 1 ? t(m.FeatureTour.Next) : completeLabel}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
